import { useEffect, useRef, useState } from 'react'
import type { MesPyg } from './tipos'
import estilos from './PygProyectos.module.css'

const ALTO = 340
const M = { arriba: 24, derecha: 24, abajo: 36, izquierda: 70 }

const COLOR = {
  ingreso: '#0AA3A8',
  costo: '#C2334D',
  utilidad: '#2D0177',
  plan: '#F59E0B',
}

function formatoPesos(valor: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Math.round(valor))
}

function formatoCorto(valor: number): string {
  const abs = Math.abs(valor)
  if (abs >= 1_000_000) {
    return `${(valor / 1_000_000).toFixed(1)}M`
  }
  if (abs >= 1_000) {
    return `${Math.round(valor / 1_000)}k`
  }
  return String(Math.round(valor))
}

function techoLimpio(v: number) {
  if (v <= 0) return 1_000_000
  const p = Math.pow(10, Math.floor(Math.log10(v)))
  for (const k of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (k * p >= v) return k * p
  }
  return 10 * p
}

type Props = {
  meses: MesPyg[]
  mesActualClave: string
}

export function GraficoProduccionPyg({ meses, mesActualClave }: Props) {
  const contenedorRef = useRef<HTMLDivElement>(null)
  const [ancho, setAncho] = useState(700)
  const [indiceHover, setIndiceHover] = useState<number | null>(null)

  useEffect(() => {
    const el = contenedorRef.current
    if (!el) return

    const ro = new ResizeObserver(([entry]) => {
      const w = Math.floor(entry.contentRect.width)
      if (w > 0) setAncho(w)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  if (meses.length === 0) return null

  // Cálculo de escalas idéntico a Arkano
  const pasados = meses.filter((m) => !m.futuro)
  const maxArriba = Math.max(
    1_000_000,
    ...pasados.map((m) => m.produccion),
    ...meses.map((m) => m.planeado),
    ...pasados.map((m) => Math.max(0, m.utilidad))
  )
  const maxAbajo = Math.max(
    1_000_000,
    ...pasados.map((m) => m.costoDirecto),
    ...pasados.map((m) => Math.max(0, -m.utilidad))
  )

  const yMax = techoLimpio(maxArriba * 1.1)
  const yMin = -techoLimpio(maxAbajo * 1.1)

  const plotW = Math.max(0, ancho - M.izquierda - M.derecha)
  const plotH = ALTO - M.arriba - M.abajo

  const y = (v: number) => M.arriba + ((yMax - v) / (yMax - yMin)) * plotH
  const yCero = y(0)

  const banda = meses.length ? plotW / meses.length : 0
  const barW = Math.min(32, Math.max(12, banda * 0.45))
  const xCentro = (i: number) => M.izquierda + banda * i + banda / 2

  // Ticks para eje Y
  const ticksY = [yMax, yMax / 2, 0, yMin / 2, yMin]

  // Puntos para línea de utilidad
  const punto = (m: MesPyg, i: number, cmd: 'M' | 'L') => `${cmd}${xCentro(i).toFixed(1)} ${y(m.utilidad).toFixed(1)}`

  const iCorteFuturo = meses.findIndex((m) => m.futuro)
  const lineaReal = iCorteFuturo <= 0
    ? meses.map((m, i) => punto(m, i, i === 0 ? 'M' : 'L')).join(' ')
    : meses.slice(0, iCorteFuturo).map((m, i) => punto(m, i, i === 0 ? 'M' : 'L')).join(' ')

  const lineaFutura = iCorteFuturo <= 0
    ? ''
    : meses.slice(iCorteFuturo - 1).map((m, k) => punto(m, iCorteFuturo - 1 + k, k === 0 ? 'M' : 'L')).join(' ')

  const mesHover = indiceHover !== null ? meses[indiceHover] : null

  return (
    <div className={estilos.cardGrafico}>
      <div className={estilos.cabeceraGrafico}>
        <div className={estilos.tituloGrafico}>
          <h3>Producción y Resultado Mes a Mes (P&G Real)</h3>
          <span className={estilos.subtituloGrafico}>
            Lo producido por avance de proyectos contra sus costos directos. Eje central en cero.
          </span>
        </div>

        {/* Leyenda interactiva */}
        <div className={estilos.leyendaGrafico}>
          <span className={estilos.itemLeyenda}>
            <i className={estilos.swatchSolido} style={{ background: COLOR.ingreso }} />
            Producción ganada
          </span>
          <span className={estilos.itemLeyenda}>
            <i className={estilos.swatchTramado} style={{ background: COLOR.costo }} />
            Costos directos
          </span>
          <span className={estilos.itemLeyenda}>
            <i className={estilos.swatchMarcaPlan} />
            Meta del plan
          </span>
          <span className={estilos.itemLeyenda}>
            <i className={estilos.swatchLineaUtil} />
            Utilidad del mes
          </span>
        </div>
      </div>

      <div
        ref={contenedorRef}
        className={estilos.svgContenedor}
        onMouseLeave={() => setIndiceHover(null)}
      >
        <svg
          width={ancho}
          height={ALTO}
          viewBox={`0 0 ${ancho} ${ALTO}`}
          style={{ overflow: 'visible' }}
        >
          <defs>
            {/* Patrón tramado a 45 grados para costos */}
            <pattern
              id="pyg-tramado-costo"
              width="6"
              height="6"
              patternTransform="rotate(45)"
              patternUnits="userSpaceOnUse"
            >
              <line x1="0" y1="0" x2="0" y2="6" stroke={COLOR.costo} strokeWidth="2.5" />
            </pattern>
          </defs>

          {/* Líneas guía horizontales del eje Y */}
          {ticksY.map((v) => {
            const yPos = y(v)
            const esCero = v === 0
            return (
              <g key={v}>
                <line
                  x1={M.izquierda}
                  x2={ancho - M.derecha}
                  y1={yPos}
                  y2={yPos}
                  stroke={esCero ? '#475569' : '#e2e8f0'}
                  strokeWidth={esCero ? 1.5 : 1}
                  strokeDasharray={esCero ? undefined : '3 3'}
                />
                <text
                  x={M.izquierda - 10}
                  y={yPos + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#64748b"
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {formatoCorto(v)}
                </text>
              </g>
            )
          })}

          {/* Barras por mes */}
          {meses.map((m, i) => {
            const xC = xCentro(i)
            const esMesActual = m.clave === mesActualClave
            const hIngreso = Math.max(0, yCero - y(m.produccion))
            const hCosto = Math.max(0, y(-m.costoDirecto) - yCero)
            const yPlan = y(m.planeado)

            return (
              <g
                key={m.clave}
                onMouseEnter={() => {
                  setIndiceHover(i)
                }}
              >
                {/* Zona transparente de hover para toda la columna */}
                <rect
                  x={M.izquierda + banda * i}
                  y={M.arriba}
                  width={banda}
                  height={plotH}
                  fill={indiceHover === i ? 'rgba(45, 1, 119, 0.04)' : 'transparent'}
                  style={{ cursor: 'pointer' }}
                />

                {/* Resalte de mes actual en el fondo */}
                {esMesActual && (
                  <rect
                    x={M.izquierda + banda * i + 1}
                    y={M.arriba}
                    width={banda - 2}
                    height={plotH}
                    fill="none"
                    stroke="var(--p-500, #3f16a2)"
                    strokeWidth="1.5"
                    strokeDasharray="4 2"
                    rx="4"
                  />
                )}

                {/* Barra de Producción (Arriba del cero, sólo meses pasados/actual) */}
                {!m.futuro && hIngreso > 0 && (
                  <rect
                    x={xC - barW / 2}
                    y={yCero - hIngreso}
                    width={barW}
                    height={hIngreso}
                    fill={COLOR.ingreso}
                    rx="2"
                  />
                )}

                {/* Barra de Costo Directo (Abajo del cero) */}
                {!m.futuro && hCosto > 0 && (
                  <rect
                    x={xC - barW / 2}
                    y={yCero}
                    width={barW}
                    height={hCosto}
                    fill="url(#pyg-tramado-costo)"
                    rx="2"
                  />
                )}

                {/* Marca / Guión del Plan (Meta de producción del mes) */}
                {m.planeado > 0 && (
                  <line
                    x1={xC - barW / 2 - 4}
                    x2={xC + barW / 2 + 4}
                    y1={yPlan}
                    y2={yPlan}
                    stroke={COLOR.plan}
                    strokeWidth="3.5"
                    strokeLinecap="round"
                  />
                )}

                {/* Etiqueta del mes en el eje X */}
                <text
                  x={xC}
                  y={ALTO - 10}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight={esMesActual ? 700 : 500}
                  fill={esMesActual ? 'var(--p-700, #250161)' : '#64748b'}
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  {m.etiqueta}
                </text>
              </g>
            )
          })}

          {/* Línea de Utilidad: Real (sólida) */}
          {lineaReal && (
            <path
              d={lineaReal}
              fill="none"
              stroke={COLOR.utilidad}
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {/* Línea de Utilidad: Futura / Proyectada (punteada) */}
          {lineaFutura && (
            <path
              d={lineaFutura}
              fill="none"
              stroke={COLOR.utilidad}
              strokeWidth="2"
              strokeDasharray="4 3"
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity="0.6"
            />
          )}

          {/* Marcadores circulares sobre los puntos de utilidad */}
          {meses.map((m, i) => {
            if (m.futuro) return null
            const xC = xCentro(i)
            const yPos = y(m.utilidad)
            return (
              <circle
                key={`dot-${m.clave}`}
                cx={xC}
                cy={yPos}
                r="4"
                fill={COLOR.utilidad}
                stroke="#ffffff"
                strokeWidth="1.5"
              />
            )
          })}
        </svg>

        {/* Tooltip interactivo flotante */}
        {mesHover && (
          <div
            className={estilos.tooltipFlotante}
            style={{
              left: `${xCentro(indiceHover!)}px`,
              top: `${y(mesHover.utilidad)}px`,
            }}
          >
            <div className={estilos.tooltipTitulo}>
              {mesHover.etiqueta} {mesHover.futuro ? '· (Proyectado)' : ''}
            </div>
            <div className={estilos.tooltipFila}>
              <span>Producción:</span>
              <b>{formatoPesos(mesHover.produccion)}</b>
            </div>
            <div className={estilos.tooltipFila}>
              <span>Meta Plan:</span>
              <b style={{ color: COLOR.plan }}>{formatoPesos(mesHover.planeado)}</b>
            </div>
            <div className={estilos.tooltipFila}>
              <span>Costo Directo:</span>
              <b style={{ color: '#f87171' }}>{formatoPesos(mesHover.costoDirecto)}</b>
            </div>
            <div className={estilos.tooltipFila}>
              <span>Utilidad / Margen:</span>
              <b style={{ color: mesHover.utilidad >= 0 ? '#34d399' : '#f87171' }}>
                {formatoPesos(mesHover.utilidad)} ({mesHover.margenPct}%)
              </b>
            </div>
            {mesHover.planeado > 0 && (
              <div className={estilos.tooltipFila}>
                <span>Cumplimiento:</span>
                <b>{mesHover.cumplimiento}%</b>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
